// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Config } from "../config";
import {
    CreatePaymentDetail,
    CreatePayrollEventRequest as CreatePayrollProjectRequest,
    GlData,
    PayDates,
    PayRate,
    PayrollAdminLocation,
    PayrollLob,
    UpdatePayrollProjectRequest,
} from "./types/payrollEventTypes";

const apiManagementRootUrl: string | undefined = Config.REACT_APP_CCL_API_MANAGEMENT_URL;

const toLocalDateString = (value: unknown): unknown => {
    if (!(value instanceof Date)) {
        return value;
    }

    if (Number.isNaN(value.getTime())) {
        return null;
    }

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const mapWorkEntryPayload = (workEntry: any) => {
    const payload = { ...workEntry };

    payload.workDate = toLocalDateString(payload.workDate);
    payload.payDate = toLocalDateString(payload.payDate);
    payload.rev_Rec_Date = toLocalDateString(payload.rev_Rec_Date);

    if (payload.audience !== undefined) {
        payload.type = payload.audience;
        delete payload.audience;
    }

    return payload;
};

// Define a service using a base URL and expected endpoints
export const cclTokenedPayrollApi = createApi({
    reducerPath: "cclTokenedPayrollApi",
    tagTypes: ["Payrolls", "workEntry", "glData", "payRates", "lobs", "paymentDetails", "locations", "payDates"],
    baseQuery: fetchBaseQuery({
        baseUrl: apiManagementRootUrl,
        prepareHeaders: (headers, { getState }) => {
            //we should probably change this.
            const token = localStorage.getItem("token") as string;
            headers.set("authorization", `Bearer ${token}`);
            return headers;
        },
    }),
    endpoints: (builder) => ({
        getAllTrainers: builder.query<any[], void>({
            query: () => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/trainer`,
                method: "GET",
            }),
        }),
        getPayrollBySessionId: builder.query<any, string>({
            query: (sessionId) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/project/event-code/${sessionId}`,
                method: "GET",
            }),
            providesTags: ["Payrolls"],
        }),
        createPayrollEvent: builder.mutation<any, CreatePayrollProjectRequest>({
            query: (payrollEvent) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/project`,
                method: "POST",
                body: payrollEvent,
            }),
            invalidatesTags: ["Payrolls"],
        }),
        getPayrollEventById: builder.query<any, string>({
            query: (eventId) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/project/event-code/${eventId}`,
                method: "GET",
            }),
            providesTags: ["Payrolls"],
        }),
        addWorkEntry: builder.mutation<any, any>({
            query: (workEntry) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/work-entry`,
                method: "POST",
                body: mapWorkEntryPayload(workEntry),
            }),
            invalidatesTags: ["workEntry"],
        }),
        getWorkEntriesByProjectId: builder.query<any, string>({
            query: (projectId) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/work-entry/project/${projectId}`,
                method: "GET",
            }),
            providesTags: ["workEntry"],
        }),
        getClientList: builder.query<any, void>({
            query: () => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/client`,
                method: "GET",
            }),
        }),
        getGlData: builder.query<GlData, void>({
            query: () => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/gl-data`,
                method: "GET",
            }),
            providesTags: ["glData"],
        }),
        getReferenceData: builder.query<any, void>({
            query: () => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/reference-data`,
                method: "GET",
            }),
            providesTags: ["lobs", "locations"],
        }),
        editWorkEntry: builder.mutation<any, any>({
            query: (workEntry) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/work-entry/${workEntry.id}`,
                method: "POST",
                body: mapWorkEntryPayload(workEntry),
            }),
            invalidatesTags: ["workEntry"],
        }),
        deleteWorkEntry: builder.mutation<any, string>({
            query: (workEntryId) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/work-entry/${workEntryId}`,
                method: "DELETE",
            }),
            invalidatesTags: ["workEntry"],
        }),
        getWorkEntryById: builder.query<any, string>({
            query: (workEntryId) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/work-entry/${workEntryId}`,
                method: "GET",
            }),
            providesTags: ["workEntry"],
        }),
        addGlGroup: builder.mutation<any, any>({
            query: (glGroup) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/gl-group`,
                method: "POST",
                body: glGroup,
            }),
            invalidatesTags: ["glData"],
        }),
        addGlCode: builder.mutation<any, any>({
            query: (glCode) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/gl-code`,
                method: "POST",
                body: glCode,
            }),
            invalidatesTags: ["glData"],
        }),
        modifyGlCode: builder.mutation<any, any>({
            query: (glCode) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/gl-code/${glCode.glCode}`,
                method: "POST",
                body: glCode,
            }),
            invalidatesTags: ["glData"],
        }),
        modifyGlGroup: builder.mutation<any, any>({
            query: (glGroup) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/gl-group/${glGroup.groupCode}`,
                method: "POST",
                body: glGroup,
            }),
            invalidatesTags: ["glData"],
        }),
        deleteGlCode: builder.mutation<any, string>({
            query: (glCode) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/gl-code/${glCode}`,
                method: "DELETE",
            }),
            invalidatesTags: ["glData"],
        }),
        deleteGlGroup: builder.mutation<any, string>({
            query: (groupCode) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/gl-group/${groupCode}`,
                method: "DELETE",
            }),
            invalidatesTags: ["glData"],
        }),
        updatePayrollProject: builder.mutation<any, UpdatePayrollProjectRequest>({
            query: (payrollProject) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/project/${payrollProject.projectId}`,
                method: "POST",
                body: payrollProject,
            }),
            invalidatesTags: ["Payrolls"],
        }),
        getPayRates: builder.query<PayRate[], void>({
            query: () => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/pay-rates`,
                method: "GET",
            }),
            providesTags: ["payRates"],
        }),
        addPayRate: builder.mutation<PayRate, PayRate>({
            query: (payRate) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/pay-rate`,
                method: "POST",
                body: payRate,
            }),
            invalidatesTags: ["payRates"],
        }),
        modifyPayRate: builder.mutation<PayRate, PayRate>({
            query: (payRate) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/pay-rate/${payRate.rateCode}`,
                method: "POST",
                body: payRate,
            }),
            invalidatesTags: ["payRates"],
        }),
        deletePayRate: builder.mutation<any, string>({
            query: (rateCode) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/pay-rate/${rateCode}`,
                method: "DELETE",
            }),
            invalidatesTags: ["payRates"],
        }),
        addLOB: builder.mutation<PayrollLob, Partial<PayrollLob>>({
            query: (lob) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/lob`,
                method: "POST",
                body: lob,
            }),
            invalidatesTags: ["lobs"],
        }),
        editLOB: builder.mutation<PayrollLob, Partial<PayrollLob> & { id: string | number }>({
            query: (lob) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/lob/${lob.id}`,
                method: "POST",
                body: lob,
            }),
            invalidatesTags: ["lobs"],
        }),
        deleteLOB: builder.mutation<any, string>({
            query: (lobId) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/lob/${lobId}`,
                method: "DELETE",
            }),
            invalidatesTags: ["lobs"],
        }),
        addPaymentDetail: builder.mutation<CreatePaymentDetail, CreatePaymentDetail>({
            query: (paymentDetail) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/payment-detail`,
                method: "POST",
                body: paymentDetail,
            }),
            invalidatesTags: ["paymentDetails"],
        }),
        editPaymentDetail: builder.mutation<CreatePaymentDetail, CreatePaymentDetail>({
            query: (paymentDetail) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/payment-detail/${paymentDetail.paymentId}`,
                method: "POST",
                body: paymentDetail,
            }),
            invalidatesTags: ["paymentDetails"],
        }),
        deletePaymentDetail: builder.mutation<any, string>({
            query: (paymentDetailId) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/payment-detail/${paymentDetailId}`,
                method: "DELETE",
            }),
            invalidatesTags: ["paymentDetails"],
        }),
        getPaymentDetailbyId: builder.query<any, string>({
            query: (paymentDetailId) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/payment-detail/${paymentDetailId}`,
                method: "GET",
            }),
            providesTags: ["paymentDetails"],
        }),
        getPaymentDetailsbyEventKey: builder.query<any[], string>({
            query: (eventKey) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/payment-detail/event/${eventKey}`,
                method: "GET",
            }),
            providesTags: ["paymentDetails"],
        }),
        addAdminLocation: builder.mutation<PayrollAdminLocation, Omit<PayrollAdminLocation, "id">>({
            query: (location) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/location`,
                method: "POST",
                body: location,
            }),
            invalidatesTags: ["locations"],
        }),
        editAdminLocation: builder.mutation<PayrollAdminLocation, PayrollAdminLocation>({
            query: ({ createdBy, modifiedBy, created, modified, ...location }) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/location/${location.locCode}`,
                method: "POST",
                body: location,
            }),
            invalidatesTags: ["locations"],
        }),
        toggleAdminLocationActiveStatus: builder.mutation<PayrollAdminLocation, PayrollAdminLocation>({
            query: ({ createdBy, modifiedBy, created, modified, ...location }) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/location/${location.locCode}/toggle-active`,
                method: "POST",
                body: location,
            }),
            invalidatesTags: ["locations"],
        }),
        togglePayRateActiveStatus: builder.mutation<PayRate, PayRate>({
            query: ({ createdBy, modifiedBy, ...payRate }) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/pay-rate/${payRate.rateCode}/toggle-active`,
                method: "POST",
                body: payRate,
            }),
            invalidatesTags: ["payRates"],
        }),
        getPayDates: builder.query<PayDates[], void>({
            query: () => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/pay-dates-from-finance`,
                method: "GET",
            }),
            providesTags: ["payDates"],
        }),
        addPayDate: builder.mutation<PayDates, any>({
            query: (payDate) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/pay-dates-from-finance`,
                method: "POST",
                body: payDate,
            }),
            invalidatesTags: ["payDates"],
        }),
        deletePayDate: builder.mutation<PayDates, string | number>({
            query: (id) => ({
                url: `ccl-access2-data/v1.0/OFC/payroll/pay-dates-from-finance/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["payDates"],
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetAllTrainersQuery,
    useGetPayrollBySessionIdQuery,
    useCreatePayrollEventMutation,
    useGetPayrollEventByIdQuery,
    useAddWorkEntryMutation,
    useGetWorkEntriesByProjectIdQuery,
    useGetClientListQuery,
    useGetGlDataQuery,
    useGetReferenceDataQuery,
    useEditWorkEntryMutation,
    useDeleteWorkEntryMutation,
    useGetWorkEntryByIdQuery,
    useLazyGetWorkEntryByIdQuery,
    useAddGlGroupMutation,
    useAddGlCodeMutation,
    useModifyGlCodeMutation,
    useModifyGlGroupMutation,
    useDeleteGlCodeMutation,
    useDeleteGlGroupMutation,
    useUpdatePayrollProjectMutation,
    useGetPayRatesQuery,
    useAddPayRateMutation,
    useModifyPayRateMutation,
    useDeletePayRateMutation,
    useAddLOBMutation,
    useEditLOBMutation,
    useDeleteLOBMutation,
    useAddPaymentDetailMutation,
    useDeletePaymentDetailMutation,
    useEditPaymentDetailMutation,
    useGetPaymentDetailbyIdQuery,
    useGetPaymentDetailsbyEventKeyQuery,
    useAddAdminLocationMutation,
    useEditAdminLocationMutation,
    useToggleAdminLocationActiveStatusMutation,
    useTogglePayRateActiveStatusMutation,
    useGetPayDatesQuery,
    useAddPayDateMutation,
    useDeletePayDateMutation,
} = cclTokenedPayrollApi;
